import { useState } from "react";
import { editorialPicks, liveMessages, rankList } from "@/mocks/data";
import type { CategoryKey } from "@/mocks/data";

const catStyle: Record<CategoryKey, string> = {
  hot: "bg-background-100 text-foreground-600",
  ent: "bg-primary-50 text-primary-700",
  film: "bg-accent-50 text-accent-700",
  music: "bg-secondary-100 text-secondary-800",
  tech: "bg-background-200 text-foreground-700",
  live: "bg-accent-100 text-accent-800",
  life: "bg-secondary-100 text-secondary-800",
};

const catName: Record<CategoryKey, string> = {
  hot: "全站",
  ent: "文娱",
  film: "影视",
  music: "音乐",
  tech: "科技",
  live: "直播",
  life: "生活",
};

const quickMsgs = ["蹲后续了", "求深扒", "前排围观", "理性讨论", "这也太离谱了", "支持一下"];

export default function Sidebar() {
  const [chats, setChats] = useState(liveMessages);
  const [draft, setDraft] = useState("");

  const send = (text: string) => {
    const t = text.trim();
    if (!t) return;
    const now = new Date();
    const time = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
    setChats((prev) => [
      ...prev.slice(-14),
      { id: `u-${Date.now()}`, name: "我", text: t.slice(0, 60), time, hot: true },
    ]);
    setDraft("");
  };

  return (
    <aside className="flex flex-col gap-4" aria-label="实时热榜与讨论">
      {/* hot ranking */}
      <section id="toplist" className="scroll-mt-24 rounded-2xl border border-background-200/70 bg-background-50 p-4 md:p-5">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
            <i className="ri-fire-fill text-primary-500" />
            实时热榜
          </h2>
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground-400">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500" />
            </span>
            每 30 秒刷新
          </span>
        </div>

        <ol className="mt-4 space-y-1">
          {rankList.map((item) => (
            <li key={item.id}>
              <a
                href="#home"
                className="group flex items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-background-100"
              >
                <span
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                    item.rank <= 3
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200 text-foreground-500"
                  }`}
                >
                  {item.rank}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground-800 group-hover:text-primary-700">
                    {item.title}
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-[11px] text-foreground-400">
                    <span className={`rounded px-1 py-px font-medium ${catStyle[item.cat]}`}>{catName[item.cat]}</span>
                    <span className="inline-flex items-center gap-0.5">
                      <i className="ri-fire-line text-primary-400" />
                      {item.heat}
                    </span>
                  </span>
                </span>
                <span
                  className={`shrink-0 text-xs font-semibold ${
                    item.up ? "text-accent-600" : "text-foreground-400"
                  }`}
                >
                  {item.delta}
                </span>
              </a>
            </li>
          ))}
        </ol>
      </section>

      {/* live discussion */}
      <section
        className="rounded-2xl border border-background-200/70 bg-background-50 p-4 md:p-5"
        aria-label="直播间实时讨论"
      >
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
            <i className="ri-live-fill text-primary-500" />
            直播间
          </h2>
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-800">
            <i className="ri-eye-line" />
            4.2 万人在线
          </span>
        </div>

        <div className="mt-3 space-y-2.5 rounded-xl bg-background-100/70 p-3">
          {chats.map((m) => (
            <div key={m.id} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
              <p className="min-w-0 text-foreground-700">
                <span className="font-semibold text-foreground-900">{m.name}：</span>
                <span className="break-words">{m.text}</span>
              </p>
              <span className="ml-auto shrink-0 text-[10px] text-foreground-400">{m.time}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {quickMsgs.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => send(q)}
              className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-medium text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
            >
              {q}
            </button>
          ))}
        </div>

        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") send(draft);
            }}
            placeholder="说点什么，一起前排围观…"
            aria-label="发表评论"
            maxLength={60}
            className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
          <button
            type="button"
            onClick={() => send(draft)}
            className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            发送
          </button>
        </div>
      </section>

      {/* editorial picks */}
      <section className="rounded-2xl border border-background-200/70 bg-background-50 p-4 md:p-5" aria-label="编辑精选">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
          <i className="ri-edit-2-fill text-accent-600" />
          编辑精选
        </h2>
        <div className="mt-3 space-y-1">
          {editorialPicks.map((p, i) => (
            <a key={p.id} href="#feature" className="group flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-background-100">
              <span className="mt-1 text-sm font-bold text-primary-300">{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700">
                  {p.title}
                </span>
                <span className="mt-1 flex items-center gap-2 text-[11px] text-foreground-400">
                  <span className="rounded bg-accent-100 px-1 py-px font-medium text-accent-800">{p.tag}</span>
                  <span className="inline-flex items-center gap-0.5">
                    <i className="ri-eye-line" />
                    {p.reads}
                  </span>
                </span>
              </span>
            </a>
          ))}
        </div>
      </section>
    </aside>
  );
}